import { useState, useEffect, useRef } from 'react'
import { geocodingApi } from '@/api'
import type { GeocodedPoint } from '@olimhub/shared'

// Places API (New) — called directly from the browser with a
// referrer-restricted key (see LESSONS_LEARNED.md §58.7), not proxied
// through our server. No new npm dependency: plain fetch, matching this
// codebase's existing hand-rolled Nominatim style.
const GOOGLE_PLACES_API_KEY = import.meta.env.VITE_GOOGLE_PLACES_API_KEY as string | undefined

export interface AddressSuggestion {
  // Google suggestions: the Places placeId, resolved via Place Details on
  // pick. Nominatim fallback suggestions: the display text itself (no
  // separate id) — select() below tells the two apart by prefix.
  key: string
  label: string
}

interface GooglePlacePrediction {
  placeId: string
  text: string
}

async function fetchGoogleAutocomplete(input: string, sessionToken: string): Promise<GooglePlacePrediction[] | null> {
  if (!GOOGLE_PLACES_API_KEY) return null
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), 5000)
  try {
    const response = await fetch('https://places.googleapis.com/v1/places:autocomplete', {
      method: 'POST',
      signal: controller.signal,
      headers: { 'Content-Type': 'application/json', 'X-Goog-Api-Key': GOOGLE_PLACES_API_KEY },
      body: JSON.stringify({ input, sessionToken, includedRegionCodes: ['il'] }),
    })
    if (!response.ok) return null
    const data = (await response.json()) as {
      suggestions?: Array<{ placePrediction?: { placeId: string; text: { text: string } } }>
    }
    return (data.suggestions ?? [])
      .filter((s) => s.placePrediction)
      .map((s) => ({ placeId: s.placePrediction!.placeId, text: s.placePrediction!.text.text }))
  } catch {
    return null
  } finally {
    clearTimeout(timeout)
  }
}

function extractCityFromGoogleComponents(components: Array<{ longText: string; types: string[] }>): string | null {
  const byType = (type: string) => components.find((c) => c.types.includes(type))?.longText ?? null
  return (
    byType('locality') ??
    byType('postal_town') ??
    byType('administrative_area_level_2') ??
    byType('administrative_area_level_1') ??
    null
  )
}

async function fetchGooglePlaceDetails(placeId: string, sessionToken: string): Promise<GeocodedPoint | null> {
  if (!GOOGLE_PLACES_API_KEY) return null
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), 5000)
  try {
    const url = `https://places.googleapis.com/v1/places/${placeId}?sessionToken=${sessionToken}`
    const response = await fetch(url, {
      signal: controller.signal,
      headers: {
        'X-Goog-Api-Key': GOOGLE_PLACES_API_KEY,
        'X-Goog-FieldMask': 'location,formattedAddress,addressComponents',
      },
    })
    if (!response.ok) return null
    const data = (await response.json()) as {
      location?: { latitude: number; longitude: number }
      addressComponents?: Array<{ longText: string; types: string[] }>
    }
    if (!data.location) return null
    return {
      latitude: data.location.latitude,
      longitude: data.location.longitude,
      city: extractCityFromGoogleComponents(data.addressComponents ?? []),
    }
  } catch {
    return null
  } finally {
    clearTimeout(timeout)
  }
}

// Debounced live suggestions, shared by every free-text location field
// (Profile, Event, Action, Group, Search). Tries Google Places Autocomplete
// (New) first — session-tokened, billed once per completed pick via
// select() below — and degrades to the existing server-side Nominatim
// /geocoding/suggest on any client-side Google failure (no key configured,
// network error, quota) so the field keeps working, just without resolved
// coordinates. Same debounce/min-length shape as useMentionAutocomplete.ts.
export function useAddressAutocomplete(query: string, enabled: boolean) {
  const [results, setResults] = useState<AddressSuggestion[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Set by the caller right before it programmatically fills the field from
  // a picked suggestion — without this, picking a suggestion changes `query`
  // just like typing does, so the effect below would immediately re-fetch
  // and reopen the same dropdown the user just closed by choosing an
  // option. Only skips once, for that exact value; typing anything else
  // afterward behaves normally again.
  const suppressNextForRef = useRef<string | null>(null)
  // Tracks whether the currently-rendered `results` came from Google (and
  // thus have a real placeId in `key`) or from the Nominatim fallback (no
  // placeId — `key` is just the display text). select() below relies on
  // this instead of re-deriving it from `results`.
  const sourceRef = useRef<'google' | 'nominatim'>('nominatim')
  // Generated fresh per typing session (first keystroke -> a pick or
  // blur-without-pick), reset to null after use — required for Google's
  // session billing to apply correctly (one Autocomplete session's worth of
  // calls + one Details call, billed once).
  const sessionTokenRef = useRef<string | null>(null)

  useEffect(() => {
    if (suppressNextForRef.current === query) {
      suppressNextForRef.current = null
      return
    }
    if (!enabled || query.trim().length < 3) {
      setResults([])
      setIsOpen(false)
      return
    }
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(async () => {
      if (!sessionTokenRef.current) sessionTokenRef.current = crypto.randomUUID()

      const predictions = await fetchGoogleAutocomplete(query, sessionTokenRef.current)
      if (predictions) {
        sourceRef.current = 'google'
        setResults(predictions.map((p) => ({ key: p.placeId, label: p.text })))
        setIsOpen(predictions.length > 0)
        return
      }

      sourceRef.current = 'nominatim'
      try {
        const suggestions = await geocodingApi.suggest(query)
        setResults(suggestions.map((s) => ({ key: s.displayName, label: s.displayName })))
        setIsOpen(suggestions.length > 0)
      } catch {
        setResults([])
        setIsOpen(false)
      }
    }, 400)
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
  }, [query, enabled])

  function close() {
    setIsOpen(false)
  }

  function suppressNextFor(value: string) {
    suppressNextForRef.current = value
  }

  // Resolves a picked suggestion to coordinates and ends the Autocomplete
  // session (billed once, no separate Geocoding API charge) — a no-op that
  // resolves to null for Nominatim fallback suggestions, since those never
  // had a placeId to resolve in the first place.
  async function select(key: string): Promise<GeocodedPoint | null> {
    const sessionToken = sessionTokenRef.current
    sessionTokenRef.current = null
    if (sourceRef.current !== 'google' || !sessionToken) return null
    return fetchGooglePlaceDetails(key, sessionToken)
  }

  return { results, isOpen, close, suppressNextFor, select }
}
